import { type ReactNode, useState } from 'react';
import { Column, Icon, Row, Text } from '~/commonComponents';
import { isValidShifts } from '~/commonComponents/InputJob/Utils';
import { isUnlikelyShift } from '~/components/Request/multiday/utils';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { ArrivalTimeField } from './ArrivalTimeField';
import { DepartureTimeField } from './DepartureTimeField';

const DEFAULT_HELPER_TEXT =
  'Set arrival 15-30 minutes before the first patient to allow time to prepare.';

interface ITimeFieldsWrapperProps {
  arrivalValue: string;
  onArrivalChange: (value: string) => void;
  departureValue: string;
  onDepartureChange: (value: string) => void;
  labelRight?: ReactNode;
  hasArrivalError?: boolean;
  hasDepartureError?: boolean;
  /**
   * Helper text rendered below the time inputs.
   * - `undefined` (default): shows the default shift-request copy
   *   ("Set arrival 15-30 minutes…").
   * - `null`: hides the helper text entirely.
   * - any other ReactNode: overrides the default with the provided content.
   */
  helperText?: ReactNode | null;
}

export const TimeFieldsWrapper = ({
  arrivalValue,
  onArrivalChange,
  departureValue,
  onDepartureChange,
  labelRight,
  hasArrivalError,
  hasDepartureError,
  helperText,
}: ITimeFieldsWrapperProps) => {
  const resolvedHelperText = helperText === undefined ? DEFAULT_HELPER_TEXT : helperText;
  const [showInvalidTimePopup, setShowInvalidTimePopup] = useState(false);
  const [showUnlikelyShiftPopup, setShowUnlikelyShiftPopup] = useState(false);
  const [pendingTimeChange, setPendingTimeChange] = useState<{
    field: 'arrival' | 'departure';
    value: string;
  } | null>(null);

  const handleTimeChange = (
    field: 'arrival' | 'departure',
    newValue: string,
    applyChange: (value: string) => void,
    otherValue: string,
  ) => {
    const arrival = field === 'arrival' ? newValue : otherValue;
    const departure = field === 'departure' ? newValue : otherValue;

    if (!arrival || !departure) {
      applyChange(newValue);
      return;
    }

    if (!isValidShifts(arrival, departure)) {
      setShowInvalidTimePopup(true);
      return;
    }

    if (isUnlikelyShift(arrival, departure)) {
      setPendingTimeChange({ field, value: newValue });
      setShowUnlikelyShiftPopup(true);
      return;
    }

    applyChange(newValue);
  };

  const handleArrivalChange = (newArrival: string) => {
    handleTimeChange('arrival', newArrival, onArrivalChange, departureValue);
  };

  const handleDepartureChange = (newDeparture: string) => {
    handleTimeChange('departure', newDeparture, onDepartureChange, arrivalValue);
  };

  const handleConfirmUnlikelyShift = () => {
    if (pendingTimeChange) {
      const applyChange =
        pendingTimeChange.field === 'arrival' ? onArrivalChange : onDepartureChange;
      applyChange(pendingTimeChange.value);
    }
    setPendingTimeChange(null);
    setShowUnlikelyShiftPopup(false);
  };

  const handleDismissUnlikelyShift = () => {
    setPendingTimeChange(null);
    setShowUnlikelyShiftPopup(false);
  };

  const displayArrival =
    pendingTimeChange?.field === 'arrival' ? pendingTimeChange.value : arrivalValue;
  const displayDeparture =
    pendingTimeChange?.field === 'departure' ? pendingTimeChange.value : departureValue;

  return (
    <>
      <Row gap={30}>
        <Column flex={1}>
          <ArrivalTimeField
            value={arrivalValue}
            onChange={handleArrivalChange}
            labelRight={labelRight}
            hasError={hasArrivalError}
          />
        </Column>
        <Column flex={1}>
          <DepartureTimeField
            value={departureValue}
            onChange={handleDepartureChange}
            labelRight={labelRight}
            hasError={hasDepartureError}
          />
        </Column>
      </Row>
      {resolvedHelperText != null && (
        typeof resolvedHelperText === 'string' ? (
          <Text fontSize={16} color="neutral_500" style={{ marginTop: -8 }}>
            {resolvedHelperText}
          </Text>
        ) : (
          resolvedHelperText
        )
      )}

      {showInvalidTimePopup && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <Icon name="warning-outline" size={80} />
            </div>
          )}
          title="Sorry!"
          description="Arrival Time cannot be after Departure Time"
          rightButtonText="Close"
          rightButtonAction={() => setShowInvalidTimePopup(false)}
          closePopup={() => setShowInvalidTimePopup(false)}
          dataTestId="invalid-time-popup"
        />
      )}

      {showUnlikelyShiftPopup && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <Icon name="warning-outline" size={80} />
            </div>
          )}
          title="Please Review Shift Time"
          description={
            <>
              <Text color="neutral_500" textAlign="center">
                The hours you&apos;ve entered appear to be outside the usual range. If they&apos;re
                accurate, click confirm. Otherwise, you can go back to make edits.
              </Text>
              <Column marginTop={18} gap={10}>
                <Row justifyContent="space-between" marginHorizontal={65}>
                  <Text color="neutral_500" bold>
                    Arrival Time:
                  </Text>
                  <Text color="neutral_500">{displayArrival}</Text>
                </Row>
                <Row justifyContent="space-between" marginHorizontal={65}>
                  <Text color="neutral_500" bold>
                    Departure Time:
                  </Text>
                  <Text color="neutral_500">{displayDeparture}</Text>
                </Row>
              </Column>
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={handleDismissUnlikelyShift}
          rightButtonText="Confirm"
          rightButtonAction={handleConfirmUnlikelyShift}
          closePopup={handleDismissUnlikelyShift}
          dataTestId="unlikely-shift-popup"
        />
      )}
    </>
  );
};
